import type { Camera, Point, Rect } from '../scene/types.ts'

export const MIN_ZOOM = 0.02
export const MAX_ZOOM = 64

export function clampZoom(z: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z))
}

export function screenToWorld(c: Camera, p: Point): Point {
  return { x: p.x / c.z + c.x, y: p.y / c.z + c.y }
}

export function worldToScreen(c: Camera, p: Point): Point {
  return { x: (p.x - c.x) * c.z, y: (p.y - c.y) * c.z }
}

export function worldRectToScreen(c: Camera, r: Rect): Rect {
  return { x: (r.x - c.x) * c.z, y: (r.y - c.y) * c.z, w: r.w * c.z, h: r.h * c.z }
}

/** Zoom about a screen point, holding the world point beneath it fixed. */
export function zoomAt(c: Camera, screen: Point, factor: number): Camera {
  const z = clampZoom(c.z * factor)
  if (z === c.z) return c
  const w = screenToWorld(c, screen)
  return { z, x: w.x - screen.x / z, y: w.y - screen.y / z }
}

/** Pan by a screen-space delta, in the direction the content moves. */
export function panByScreen(c: Camera, dx: number, dy: number): Camera {
  return { ...c, x: c.x - dx / c.z, y: c.y - dy / c.z }
}

/** Fit a world rect into a viewport, centered, leaving `padding` screen px. */
export function fitRect(
  r: Rect,
  viewW: number,
  viewH: number,
  padding = 48,
): Camera {
  const availW = Math.max(1, viewW - padding * 2)
  const availH = Math.max(1, viewH - padding * 2)
  const z = r.w > 0 && r.h > 0 ? clampZoom(Math.min(availW / r.w, availH / r.h)) : 1
  return {
    z,
    x: r.x + r.w / 2 - viewW / 2 / z,
    y: r.y + r.h / 2 - viewH / 2 / z,
  }
}

/** The world rect currently visible in a viewport of the given size. */
export function visibleWorldRect(c: Camera, viewW: number, viewH: number): Rect {
  return { x: c.x, y: c.y, w: viewW / c.z, h: viewH / c.z }
}

/**
 * CSS transform for the world layer. Children are positioned at world
 * coordinates; with `transform-origin: 0 0` this maps them to screen space.
 */
export function worldTransform(c: Camera): string {
  return `translate(${-c.x * c.z}px, ${-c.y * c.z}px) scale(${c.z})`
}
